import { useState, type FormEvent } from "react";
import { api, ApiError } from "../../api";
import { ErrorNote, Loading, useApi } from "../../components/ui";
import { ProjectList, type ProjectSummary } from "./Dashboard";
import { usePickers } from "./shared";

export function StaffProjects() {
  const [all, setAll] = useState(false);
  const { data, error, loading, reload } = useApi<{ projects: ProjectSummary[] }>(`/staff/projects${all ? "" : "?inFlight=1"}`);
  const [editing, setEditing] = useState<ProjectSummary | "new" | null>(null);
  return (
    <div className="page">
      <div className="page-head">
        <h1>Projects</h1>
        <button className="btn btn-primary" onClick={() => setEditing("new")}>New project</button>
      </div>
      {editing && <ProjectForm project={editing === "new" ? null : editing} onDone={() => (setEditing(null), reload())} />}
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={!all} className={!all ? "active" : ""} onClick={() => setAll(false)}>In flight</button>
        <button role="tab" aria-selected={all} className={all ? "active" : ""} onClick={() => setAll(true)}>All</button>
      </div>
      <ErrorNote message={error} />
      {loading && !data ? <Loading /> : (
        <div className="card">
          <ProjectList projects={data?.projects ?? []} />
          {data && data.projects.length > 0 && (
            <div className="row" style={{ marginTop: 12, gap: 6 }}>
              <span className="small muted">Edit:</span>
              {data.projects.map((p) => (
                <button key={p.id} className="btn btn-small" onClick={() => setEditing(p)}>{p.name}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProjectForm({ project, onDone }: { project: ProjectSummary | null; onDone: () => void }) {
  const pickers = usePickers();
  const full = useApi<{ project: { description: string; owner_id: string | null; start_date: string | null } }>(project ? `/staff/projects/${project.id}` : null);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const s = (k: string) => (f.get(k) ? String(f.get(k)) : null);
    const body = { name: s("name"), description: s("description") ?? "", orgId: s("orgId"), status: s("status"), ownerId: s("ownerId"), startDate: s("startDate"), targetDate: s("targetDate") };
    try {
      if (project) await api(`/staff/projects/${project.id}`, { method: "PATCH", json: body });
      else await api("/staff/projects", { method: "POST", json: body });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save.");
    }
  };
  if (project && full.loading) return <Loading />;
  const p = full.data?.project;
  return (
    <form className="card stack" onSubmit={submit} style={{ marginBottom: 16 }} key={project?.id ?? "new"}>
      <h2>{project ? `Edit ${project.name}` : "New project"}</h2>
      <div className="grid grid-2">
        <div className="field">
          <label htmlFor="p-name">Name</label>
          <input id="p-name" name="name" type="text" required maxLength={160} defaultValue={project?.name} />
        </div>
        <div className="field">
          <label htmlFor="p-org">Customer</label>
          <select id="p-org" name="orgId" defaultValue={project?.orgId ?? ""}>
            <option value="">Internal</option>
            {pickers.orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="p-status">Status</label>
          <select id="p-status" name="status" defaultValue={project?.status ?? "planned"}>
            <option value="planned">Planned</option>
            <option value="active">Active</option>
            <option value="blocked">Blocked</option>
            <option value="done">Done</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="p-owner">Owner</label>
          <select id="p-owner" name="ownerId" defaultValue={p?.owner_id ?? ""}>
            <option value="">None</option>
            {pickers.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="p-start">Start</label>
          <input id="p-start" name="startDate" type="date" defaultValue={p?.start_date ?? ""} />
        </div>
        <div className="field">
          <label htmlFor="p-target">Target date</label>
          <input id="p-target" name="targetDate" type="date" defaultValue={project?.targetDate ?? ""} />
        </div>
      </div>
      <div className="field">
        <label htmlFor="p-desc">Description</label>
        <textarea id="p-desc" name="description" maxLength={8000} defaultValue={p?.description ?? ""} />
      </div>
      {error && <p className="error-text" role="alert">{error}</p>}
      <div className="row">
        <button className="btn btn-primary">Save</button>
        <button type="button" className="btn" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}
